<script lang="ts">
  import {
    ChevronDown,
    ShieldAlert,
    Calendar,
    Plane,
    Coffee,
    BarChart3,
    Sparkles
  } from "lucide-svelte";
  import { confidenceTier, type BucketSummary } from "./bucketize";
  import type { WalletBenefit, WalletCard } from "../wallet/types";
  import type { BenefitFeedback } from "./feedback";
  import BenefitCard from "./BenefitCard.svelte";
  import { cn } from "./lib/utils";

  export let bucket: BucketSummary;
  export let expanded: boolean;
  export let pendingExpanded: boolean;
  export let selectedCards: WalletCard[];
  export let feedbackMap: Record<string, BenefitFeedback>;
  export let onToggle: () => void;
  export let onTogglePending: () => void;
  export let onFeedback: (benefitId: string, kind: "used" | "rule_wrong") => void;
  export let onEditProfile: () => void = () => {};

  $: counts = (() => {
    let high = 0;
    let medium = 0;
    for (const b of bucket.benefits) {
      const t = confidenceTier(b);
      if (t === "high") high += 1;
      else if (t === "medium") medium += 1;
    }
    return { high, medium, low: bucket.pendingReview.length };
  })();

  $: totalShown = bucket.benefits.length + bucket.pendingReview.length;

  function toneVar(id: string): string {
    switch (id) {
      case "weekly":
        return "var(--tone-weekly)";
      case "travel":
        return "var(--tone-travel)";
      case "monthly":
        return "var(--tone-monthly)";
      case "yearly":
        return "var(--tone-yearly)";
      default:
        return "var(--tone-weekly)";
    }
  }

  function bucketIcon(id: string) {
    if (id === "weekly") return Coffee;
    if (id === "travel") return Plane;
    if (id === "monthly") return Calendar;
    if (id === "yearly") return BarChart3;
    return Sparkles;
  }

  function dayOfWeek(): number {
    return new Date().getDay();
  }
  const WEEK = ["日", "一", "二", "三", "四", "五", "六"];

  function monthDots(count: number): string[] {
    return Array.from({ length: Math.min(count, 5) }, (_, i) => `dot-${i}`);
  }
</script>

<article
  class={cn(
    "anim-fade-in-up overflow-hidden rounded-2xl border bg-card transition-all",
    !bucket.visible && "opacity-55"
  )}
  style="--tone: {toneVar(bucket.def.id)};"
>
  <button
    type="button"
    class="relative flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-foreground/[0.02]"
    on:click={onToggle}
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-0 left-0 w-1.5"
      style="background: linear-gradient(180deg, hsl(var(--tone) / 0.85), hsl(var(--tone) / 0.2));"
    ></div>

    <!-- emoji glyph in a tinted circle -->
    <span
      class="ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-lg"
      style="background: hsl(var(--tone) / 0.16);"
    >
      {bucket.def.emoji}
    </span>

    <span class="min-w-0 flex-1">
      <span class="block truncate text-sm font-semibold tracking-tight text-foreground">
        {bucket.def.title}
      </span>
      <span class="block truncate text-[11px] text-muted-foreground">
        {bucket.def.description}
      </span>
    </span>

    <!-- per-bucket collapsed preview -->
    <div class="hidden items-center gap-2 text-[10px] text-muted-foreground sm:flex">
      {#if bucket.def.id === "weekly"}
        <div class="flex items-center gap-0.5">
          {#each WEEK as d, i}
            <span
              class={cn(
                "flex size-4 items-center justify-center rounded-sm text-[9px] font-medium",
                i === dayOfWeek()
                  ? "bg-[hsl(var(--tone))] text-background"
                  : "bg-foreground/5 text-muted-foreground"
              )}
            >
              {d}
            </span>
          {/each}
        </div>
      {:else if bucket.def.id === "travel"}
        <div class="relative h-5 w-10">
          <div
            class="absolute left-0 top-1 h-3 w-5 rounded-sm bg-[hsl(var(--tone)/0.35)] ring-1 ring-[hsl(var(--tone)/0.5)]"
            style="transform: rotate(-8deg);"
          ></div>
          <div
            class="absolute left-1.5 top-0.5 h-3 w-5 rounded-sm bg-[hsl(var(--tone)/0.55)] ring-1 ring-[hsl(var(--tone)/0.7)]"
            style="transform: rotate(2deg);"
          ></div>
          <div
            class="absolute left-3 top-1.5 h-3 w-5 rounded-sm bg-[hsl(var(--tone)/0.85)] ring-1 ring-[hsl(var(--tone))]"
            style="transform: rotate(10deg);"
          ></div>
        </div>
      {:else if bucket.def.id === "monthly"}
        <div class="grid grid-cols-7 gap-[2px]">
          {#each Array(14) as _, i}
            <span
              class={cn(
                "size-1.5 rounded-[2px]",
                i === 3 || i === 9 ? "bg-[hsl(var(--tone))]" : "bg-foreground/10"
              )}
            ></span>
          {/each}
        </div>
      {:else if bucket.def.id === "yearly"}
        <div class="flex items-end gap-0.5 h-5">
          {#each [3, 5, 4, 6, 2, 5] as h}
            <span
              class="w-1 rounded-t-sm bg-[hsl(var(--tone)/0.7)]"
              style="height: {h * 3}px;"
            ></span>
          {/each}
        </div>
      {/if}
    </div>

    <span class="shrink-0 text-[11px] font-medium text-muted-foreground">
      {#if !bucket.visible}
        已折叠
      {:else if totalShown === 0}
        空
      {:else}
        <span class="hidden sm:inline">
          {counts.high + counts.medium} 主榜{#if counts.low > 0} · {counts.low} 待核{/if}
        </span>
        <span class="sm:hidden">
          {counts.high + counts.medium}{#if counts.low > 0}·{counts.low}{/if}
        </span>
      {/if}
    </span>

    <ChevronDown
      class={cn(
        "size-4 shrink-0 text-muted-foreground transition-transform duration-300",
        expanded && "rotate-180"
      )}
    />
  </button>

  {#if expanded}
    <div class="anim-fade-in-up border-t border-dashed border-foreground/10 px-5 pb-5 pt-4">
      {#if bucket.def.id === "yearly"}
        <p class="mb-3 text-sm text-muted-foreground">
          各卡年费基础信息。续卡决策季会在年底前提醒你。
        </p>
        <ul class="space-y-2">
          {#each selectedCards as card}
            <li class="flex items-start justify-between gap-3 rounded-xl bg-secondary/60 px-3 py-2.5">
              <div class="min-w-0">
                <strong class="block truncate text-sm font-medium text-foreground">
                  {card.shortName || card.name}
                </strong>
                <span class="mt-0.5 block text-[11px] text-muted-foreground">{card.issuer}</span>
              </div>
              <span class="shrink-0 text-right text-[11px] text-muted-foreground">{card.annualFeeText}</span>
            </li>
          {/each}
        </ul>
      {:else if totalShown === 0 && bucket.visible}
        <p class="text-sm text-muted-foreground">
          这个 bucket 今天没有值得你做的事。这是好事，不是 bug——少一个决策。
        </p>
      {:else if !bucket.visible}
        <p class="text-sm text-muted-foreground">
          画像里你说不需要这类。点
          <button
            type="button"
            on:click={onEditProfile}
            class="text-primary underline-offset-4 hover:underline"
          >
            改画像
          </button>
          打开。
        </p>
      {:else}
        {#if bucket.benefits.length > 0}
          <ul class="space-y-3">
            {#each bucket.benefits as b, i}
              <div class="anim-fade-in-up" style="animation-delay: {i * 60}ms;">
                <BenefitCard
                  benefit={b}
                  {selectedCards}
                  {feedbackMap}
                  onFeedback={onFeedback}
                />
              </div>
            {/each}
          </ul>
        {/if}

        {#if bucket.pendingReview.length > 0}
          <div class={cn(bucket.benefits.length === 0 ? "" : "mt-4")}>
            <button
              type="button"
              class="focus-amber inline-flex items-center gap-1.5 rounded-full bg-warn/10 px-3 py-1.5 text-[11px] font-medium text-warn transition-colors hover:bg-warn/15"
              on:click={onTogglePending}
            >
              <ShieldAlert class="size-3.5" />
              待核实 {bucket.pendingReview.length} 条
              <ChevronDown
                class={cn(
                  "size-3 transition-transform duration-300",
                  pendingExpanded && "rotate-180"
                )}
              />
            </button>
            <p class="mt-1.5 text-[11px] text-muted-foreground/80">
              规则来源未达到主榜置信度。展开仅供参考，不作为推荐依据。
            </p>
            {#if pendingExpanded}
              <ul class="anim-fade-in-up mt-3 space-y-2">
                {#each bucket.pendingReview as b, i}
                  <div class="anim-fade-in-up" style="animation-delay: {i * 60}ms;">
                    <BenefitCard
                      benefit={b}
                      {selectedCards}
                      {feedbackMap}
                      muted={true}
                      onFeedback={onFeedback}
                    />
                  </div>
                {/each}
              </ul>
            {/if}
          </div>
        {/if}
      {/if}

      {#if bucket.hiddenCount > 0 && bucket.visible}
        <p class="mt-4 text-[11px] text-muted-foreground/70">
          画像过滤掉了 {bucket.hiddenCount} 条（对你不适用）。
        </p>
      {/if}
    </div>
  {/if}
</article>
